import { useState } from 'react'

import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import { DatePicker } from '@mui/x-date-pickers'

import type { Entity } from '@models/Entity'
import { addWeeks } from '@util/date-util'

import type { AuthenticatedUser } from '../../../../auth/types/AuthenticatedUser'
import { SelectString } from '../../../../core/components/SelectString'
import { StyledError } from '../../../../theme/components/StyledError'
import { useCreateSubprocesForEntity } from '../../hooks/useCreateSubprocesForEntity'
import type { Subproces } from '../../types/Subproces'

export interface Props {
  entity: Entity
  aannemers: string[]
  user: AuthenticatedUser
  onClose: () => void
}

export const CreateSchouw = ({
  entity,
  aannemers = [],
  user,
  onClose,
}: Props) => {
  const { mutate: createSubprocesForEntity, error } =
    useCreateSubprocesForEntity()

  const [errors, setErrors] = useState<
    Partial<Record<keyof Subproces, boolean>>
  >({})

  /* v8 ignore next - TODO: missing coverage -- @preserve */
  const [assignedTo, setAssignedTo] = useState(
    aannemers.includes(user.organisatie)
      ? user.organisatie
      : aannemers.length === 1
        ? aannemers[0]
        : '',
  )
  const [inplannenVoorDatum, setInplannenVoorDatum] = useState<Date | null>(
    addWeeks(new Date(), 1),
  )
  const [planDatum, setPlanDatum] = useState<Date | null>(null)
  const [showPlandatum, setShowPlandatum] = useState(false)

  const validate = () => {
    const assignedToError = !assignedTo
    const dueDateError =
      (!inplannenVoorDatum && !planDatum) ||
      (!!inplannenVoorDatum && !!planDatum)

    setErrors({
      assignedTo: assignedToError,
      dueDate: dueDateError,
    })

    return !assignedToError && !dueDateError
  }

  const onSubmitClick = () => {
    if (validate()) {
      const proces: Partial<Subproces> = {
        type: 'schouw',
        assignedTo,
        status: inplannenVoorDatum ? 'created' : 'planned',
        dueDate: planDatum
          ? (planDatum as unknown as string)
          : (inplannenVoorDatum as unknown as string),
      }

      const body = {
        entity,
        subprocesInfo: proces,
      }

      createSubprocesForEntity(body, { onSuccess: () => onClose() })
    }
  }

  return (
    <>
      <DialogContent>
        <StyledError error={error} />
        <Grid container sx={{ width: '100%' }} spacing={1}>
          <Grid size={{ xs: 6 }}>
            <SelectString
              options={aannemers}
              onChange={(value) => {
                if (value) {
                  setAssignedTo(value)
                }
              }}
              value={assignedTo}
              label="Toegewezen aan"
              error={errors.assignedTo}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <DatePicker
              label="Schouw inplannen voor"
              value={inplannenVoorDatum}
              onChange={(e) => {
                setInplannenVoorDatum(e)
                setPlanDatum(null)
              }}
              disabled={true}
              displayWeekNumber
              disablePast
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'standard',
                  sx: { pr: 1 },
                  error: errors.dueDate,
                  fullWidth: true,
                },
              }}
            />
          </Grid>
        </Grid>
        <FormControl sx={{ mt: 3 }}>
          <FormControlLabel
            value={showPlandatum}
            control={<Switch checked={showPlandatum} />}
            label={'De schouwdatum is al bekend'}
            onChange={() => {
              if (!showPlandatum) {
                setInplannenVoorDatum(null)
              }
              if (showPlandatum) {
                setInplannenVoorDatum(addWeeks(new Date(), 1))
              }
              setShowPlandatum(!showPlandatum)
            }}
          />
        </FormControl>

        {showPlandatum && (
          <DatePicker
            label="Uitvoeren op"
            value={planDatum}
            onChange={(e) => {
              setPlanDatum(e)
              setInplannenVoorDatum(null)
            }}
            displayWeekNumber
            disablePast
            slotProps={{
              field: { clearable: true },
              textField: {
                variant: 'standard',
                sx: { pr: 1 },
                error: errors.dueDate,
                fullWidth: true,
                helperText:
                  'Een van de twee datums kan ingevuld worden, maar niet beide.',
              },
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose} autoFocus>
          Annuleren
        </Button>
        <Button onClick={onSubmitClick} variant={'contained'}>
          Opslaan
        </Button>
      </DialogActions>
    </>
  )
}
